import {Drawer} from 'components/customer/Product/entity/Drawer';
import {
    MIXED,
    sharedValue,
    shortRunner,
    toNumber,
} from 'components/customer/Product/Drawer/helpers';

// The share of the height each drawer was given, while they all have the same
// one. Auto shares the height in equal slots, not equal gaps, so this is what
// it can truly say about the spacing.
export const equalShare = (drawers: Drawer[]): number | null => {
    const shares = drawers.map((drawer) =>
        toNumber(drawer?.drawer_face_height)
    );

    if (shares.length === 0 || !shares.every((share) => share > 0)) {
        return null;
    }

    const first = shares[0];

    return shares.every((share) => Math.abs(share - first) < 0.05)
        ? first
        : null;
};

// the shares, once the layout has given every drawer one
const sharesKnown = (drawers: Drawer[]) =>
    drawers.length > 0 &&
    drawers.every((drawer) => toNumber(drawer?.drawer_face_height) > 0);

const onClearance = (drawer: Drawer, clearance: number | null) =>
    clearance !== null &&
    Math.abs(toNumber(drawer?.position_to_bottom) - clearance) < 0.05;

// Whether a saved layout was set by hand. Nothing records that, but today's
// layout gives every drawer the same share, and moving a drawer changes it;
// a lone drawer instead leaves the inner bottom clearance it is put on.
// Undecided, null, until the shares and that clearance are known.
export const isCustomised = (
    drawers: Drawer[],
    bottomClearance: number | null
): boolean | null => {
    if (!sharesKnown(drawers)) return null;

    if (drawers.length === 1) {
        return bottomClearance === null
            ? null
            : !onClearance(drawers[0], bottomClearance);
    }

    return equalShare(drawers) === null;
};

// the Auto view's line, read off the drawers as they stand
export const innerDrawerSummary = (drawers: Drawer[], runnerField: string) => {
    const count = drawers.length;
    const plural = count === 1 ? '' : 's';
    const runner = sharedValue(drawers, runnerField);

    let summary = `${count} drawer${plural}`;

    if (runner === MIXED) {
        summary += ' · mixed runners';
    } else if (shortRunner(runner) !== '') {
        summary += ` · ${shortRunner(runner)} runner${plural}`;
    }

    return summary;
};
